import React, { useState } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';

const Clinic = () => {
  const { clinicImages } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % clinicImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + clinicImages.length) % clinicImages.length);
  };

  // Translate clinic image titles
  const getTranslatedTitle = (title) => {
    const titleTranslations = {
      'Unsere Klinik': language === 'de' ? 'Unsere Klinik' : 'Our Clinic',
      'Moderner Flur': language === 'de' ? 'Moderner Flur' : 'Modern Hallway',
      'Steriler OP-Raum': language === 'de' ? 'Steriler OP-Raum' : 'Sterile Operating Room',
      'Eleganter Wartebereich': language === 'de' ? 'Eleganter Wartebereich' : 'Elegant Waiting Area',
    };
    return titleTranslations[title] || title;
  };

  return (
    <section id="klinik" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute bottom-0 left-1/3 w-[600px] h-[600px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {language === 'de' ? 'Modernste Ausstattung' : 'State-of-the-Art Equipment'}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Unsere ' : 'Our '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Klinik' : 'Clinic'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Erleben Sie eine Atmosphäre, die Komfort und modernste Medizintechnik vereint.'
              : 'Experience an atmosphere that combines comfort and state-of-the-art medical technology.'}
          </p>
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Main Image */}
          <div className="relative group rounded-2xl overflow-hidden aspect-video lg:aspect-auto lg:row-span-2">
            <img
              src={clinicImages[currentIndex].src}
              alt={clinicImages[currentIndex].alt}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Navigation Arrows */}
            <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 flex justify-between">
              <button
                onClick={prevImage}
                className={`w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300 opacity-0 group-hover:opacity-100 ${
                  isDark ? 'bg-black/30 hover:bg-[#7BA7C2]/50' : 'bg-white/30 hover:bg-[#5A8BA6]/50'
                }`}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextImage}
                className={`w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300 opacity-0 group-hover:opacity-100 ${
                  isDark ? 'bg-black/30 hover:bg-[#7BA7C2]/50' : 'bg-white/30 hover:bg-[#5A8BA6]/50'
                }`}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Title */}
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-white text-xl font-semibold">{getTranslatedTitle(clinicImages[currentIndex].title)}</p>
            </div>

            {/* Expand Button */}
            <Dialog>
              <DialogTrigger asChild>
                <button className={`absolute top-4 right-4 w-10 h-10 rounded-lg backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300 opacity-0 group-hover:opacity-100 ${
                  isDark ? 'bg-black/30 hover:bg-[#7BA7C2]/50' : 'bg-white/30 hover:bg-[#5A8BA6]/50'
                }`}>
                  <Expand className="w-5 h-5" />
                </button>
              </DialogTrigger>
              <DialogContent className={`max-w-4xl ${isDark ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'}`}>
                <img
                  src={clinicImages[currentIndex].src}
                  alt={clinicImages[currentIndex].alt}
                  loading="lazy"
                  className="w-full h-auto rounded-lg"
                />
              </DialogContent>
            </Dialog>
          </div>

          {/* Thumbnail Grid */}
          <div className="grid grid-cols-2 gap-4">
            {clinicImages.map((image, index) => (
              <button
                key={image.id}
                onClick={() => setCurrentIndex(index)}
                className={`relative rounded-xl overflow-hidden aspect-video group transition-all duration-300 ${
                  currentIndex === index
                    ? isDark 
                      ? 'ring-2 ring-[#7BA7C2] ring-offset-2 ring-offset-[#0a0a0a]'
                      : 'ring-2 ring-[#5A8BA6] ring-offset-2 ring-offset-white'
                    : 'hover:opacity-80'
                }`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-sm font-medium">{getTranslatedTitle(image.title)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: language === 'de' ? 'Sterile OP-Räume' : 'Sterile Operating Rooms', value: '2' },
            { label: language === 'de' ? 'Untersuchungsraum' : 'Examination Room', value: '1' },
            { label: language === 'de' ? 'Jahre Erfahrung' : 'Years Experience', value: '15+' },
            { label: language === 'de' ? 'Zufriedene Patienten' : 'Satisfied Patients', value: '5000+' },
          ].map((stat, index) => (
            <div
              key={index}
              className={`p-6 rounded-xl border text-center transition-colors duration-300 ${
                isDark 
                  ? 'bg-white/[0.02] border-white/5 hover:border-[#7BA7C2]/20'
                  : 'bg-white border-gray-200 shadow-sm hover:border-[#5A8BA6]/30'
              }`}
            >
              <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>{stat.value}</div>
              <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Clinic;